import { type RoleKeys } from "../../../constants/roles.ts"
import { type PermissionProjectPermissions } from "../../SettingsPermissions/types/permissions.types.ts"
import { type ProjectOpenRead } from "../redux/project-page-reads.ts"
import { getInvoiceSnapshotVisibility } from "./get-invoice-snapshot-visibility.tsx"
import { canViewFinanceSnapshot } from "./project-finance-visibility.ts"
import { type ProjectSectionKeys } from "./project-visible-tabs.ts"

export const TABS_WITH_SNAPSHOT: ProjectSectionKeys[] = [
  "milestones",
  "finance",
  "invoices",
  "tasks",
  "commissions"
]

export const getProjectSnapshotVisibility = (
  projectPermissions: PermissionProjectPermissions | undefined,
  userRole: RoleKeys
) => {
  const showFinanceSnapshot = canViewFinanceSnapshot(projectPermissions)
  const showInvoiceSnapshot = getInvoiceSnapshotVisibility({
    userRole,
    projectPermissions
  })
  const showMilestoneSnapshot =
    !!projectPermissions?.milestones?.project_milestone_view

  return {
    showSalesSnapshot: !!projectPermissions?.features?.project_snapshot_sales,
    showCommissionSnapshot:
      !!projectPermissions?.features?.project_snapshot_commissions,
    showFinanceSnapshot,
    showInvoiceSnapshot,
    showMilestoneSnapshot,
    hasSnapshot:
      showFinanceSnapshot || showInvoiceSnapshot || showMilestoneSnapshot
  }
}

/**
 * The reads the screens a project opens on will ask for, decided by the same
 * rules that decide whether those screens show.
 */
export const getProjectOpenReads = ({
  projectPermissions,
  userRole,
  tab,
  snapshotCollapsed
}: {
  projectPermissions: PermissionProjectPermissions | undefined
  userRole: RoleKeys
  tab: ProjectSectionKeys | null | undefined
  snapshotCollapsed: boolean
}): ProjectOpenRead[] => {
  const reads: ProjectOpenRead[] = []
  const snapshot = getProjectSnapshotVisibility(projectPermissions, userRole)

  if (
    snapshot.hasSnapshot &&
    !snapshotCollapsed &&
    !!tab &&
    TABS_WITH_SNAPSHOT.includes(tab)
  ) {
    if (snapshot.showMilestoneSnapshot) reads.push("milestoneSnapshot")
    if (snapshot.showFinanceSnapshot) {
      reads.push("financialSnapshot", "financialAlerts")
    }
    if (snapshot.showInvoiceSnapshot) reads.push("invoiceSnapshot")
  }

  if (
    tab === "milestones" &&
    !!projectPermissions?.projects?.project_view &&
    !!projectPermissions?.milestones?.project_milestone_view
  ) {
    reads.push("milestoneDependencies", "milestonesFilterOptions")
    if (projectPermissions?.planned_finances?.project_planned_finance_view) {
      reads.push("financeFilterOptions")
    }
  }

  return reads
}
